/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { invoke, construct } from "./invoke.js";
/**
 * Methods for interacting with ToDesktop browser views.
 *
 * @remarks
 * This package exposes a number of methods for interacting with ToDesktop
 * browser views, allowing you to partition a single window into several
 * independent WebViews, and provide messaging between them.
 *
 * Views are useful, for example, to create a tabbed interface for your app
 * split on the context your user wants to access.
 *
 * @experimental
 * @public
 * @packageDocumentation
 */
/**
 * Creates a new BrowserView.
 *
 * @param options - BrowserView constructor options.
 * @returns Identifier for the newly created BrowserView.
 * @public
 */
export async function create(options) {
    return construct({
        namespace: "BrowserView",
        args: [options],
    });
}
/**
 * Resizes and moves the BrowserView to the supplied bounds relative to the window.
 *
 * @param options - ref: Reference for the BrowserView; bounds: `x`, `y`, `width`, and `height` bounds for the BrowserView;
 * @public
 */
export async function setBounds({ ref, bounds, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserView",
        method: "setBounds",
        args: [bounds],
    });
}
/**
 * Resizes the BrowserView when its parent BrowserWindow dimensions change.
 *
 * @param options - ref: Reference for the BrowserView; dimensions: `x`, `y`, `width`, and `height` dimensions for the BrowserView;
 * @public
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export async function setAutoResize({ ref, dimensions, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserView",
        method: "setAutoResize",
        args: [dimensions],
    });
}
/**
 * Retrieves a BrowserView's WebContents.
 *
 * @param options - ref: Reference for the BrowserView;
 * @returns Identifier for the BrowserView's WebContents.
 * @public
 */
export async function getWebContents({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserView",
        property: "webContents",
    });
}
